import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toastActions } from "./toast";
import { getRandId, readURIFromFile } from "../libs";
import { ItemRemovalService } from "../api/itemRemoval";
import { currentUserActions } from "./currentUser";
import { MaxPhotoUpload, MaxUploadFileSize, MaxRegenCount } from "../constants";
import { OpenAPI } from "../api/core/OpenAPI";
import { NavigateFunction } from "react-router-dom";
import { applyWatermark, WatermarkOptions, dataURLtoBlob, blobToDataURL, fetchImageAsBlob, convertServiceSettingsToWatermarkOptions, isWatermarkEnabled } from '../utils/watermark';
import { WATERMARK_TYPE } from './serviceSettings';
import { selectServiceSettings } from '../selectors';
import { ItemRemovalServerResponse, ThunkAPIType } from '../types/watermark';
/* eslint-enable */

// アセット画像をインポート
import pho_industrial from "../../assets/images/pho_industrial.webp";
import pho_luxury from "../../assets/images/pho_luxury.webp";
import pho_modern from "../../assets/images/pho_modern.webp";
import pho_nordic from "../../assets/images/pho_nordic.webp";

// TODO: テストが終わったら消す - モックテスト機能
// 使用方法: mockTestConfig.USE_MOCK_SERVER_RESPONSE を false にすると本番APIを使用
// テスト完了後は mockTestConfig オブジェクト全体と、それを使用している箇所を削除してください
const mockTestConfig = {
    // この値を true にすると、実際のサーバーレスポンスをモック化します
    // USE_MOCK_MODEがfalseの時のみ有効です
    USE_MOCK_SERVER_RESPONSE: false,

    // モック用の画像URL（実際の画像URLを使用）
    MOCK_IMAGE_URLS: [
        'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=600',  // ソファのリビング
        'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600',  // モダンなリビング
        'https://images.unsplash.com/photo-1565182999561-18d7dc61c393?w=800&h=600', // 明るいリビング
    ],

    // サーバーレスポンスをモック化する関数
    createMockServerResponse(itemRemovalId: number, beforeImageId: number): ItemRemovalServerResponse {
        const mockUrl = this.MOCK_IMAGE_URLS[Math.floor(Math.random() * this.MOCK_IMAGE_URLS.length)]

        return {
            success: true,
            item_removal_id: itemRemovalId,
            before_image_id: beforeImageId,
            after_image_id: Math.floor(Math.random() * 100000) + 1000,
            after_image_url: mockUrl,
            after_image_file_name: `mock_image_${Date.now()}.jpg`,
            file_name: `mock_image_${Date.now()}.jpg`,
            headers: {
                'x-render-id': `mock-render-${Date.now()}`
            }
        }
    }
}


// 再実行用の画像配列
const regeneratedImages = [pho_modern, pho_luxury, pho_nordic, pho_industrial];


export interface ItemImage {
  id?: number;
  before_image_id?: number;
  stubId: number;
  file: File;
  uri: string;
  serverURI?: string;
  isProcessedFail: boolean;
  isProcessed: boolean;
  maxRegenCount?: number;
  render_id?: string;
}

export interface AfterImage {
  id: number;
  item_image_id: number;
  before_image_id?: number;
  serverURI: string;
  fileName: string;
  stubId?: number;
  // ウォーターマーク関連
  watermarkEnabled?: boolean;
  watermarkedURI?: string;
  isProcessingWatermark?: boolean;
}

export interface ItemRemovalState {
  isLoading: boolean;
  stubId: number;
  selectedItemImage?: number;
  selectedAfterImage?: number;
  itemImages: ItemImage[];
  afterImages: AfterImage[];
  isConfirmPopupShow: boolean;
  isPhotoTooBigPopupShow: boolean;
  isRegenPopupShow: boolean;
  isClearAllPopupShow: boolean;
  maxExecTimePerImage: number; // seconds
  runTime: number;
  selectedRegenImage?: number;
  afterImageRef: HTMLImageElement | null;
  beforeImageRef: HTMLImageElement | null;
  // ウォーターマーク関連
  watermarkGlobalEnabled: boolean;
  processingWatermarks: string[]; // 処理中の画像ID
}

// アプリケーションのRootState型
interface RootState {
  itemRemoval: ItemRemovalState;
  [key: string]: unknown;
}

// サーバー応答の型
type ThunkDispatchType = (action: unknown) => void;
interface ThunkAPIType {
  getState: () => RootState;
  dispatch: ThunkDispatchType;
}

// FileListを受け取り、処理を行う非同期アクション
const addItemImages = createAsyncThunk<void, {stubId: string, files: FileList}, { state: RootState }>(
  "addItemImages",
  async (params, thunkAPI) => {
    const { itemImages } = thunkAPI.getState().itemRemoval;
    const { files, stubId } = params;
    const newImages: ItemImage[] = [...itemImages];

    for (const file of files) {
      if (file.type !== "image/png" && file.type !== "image/jpeg" && file.type !== "image/webp")
        continue;
      if (file.size >= MaxUploadFileSize) {
        thunkAPI.dispatch(itemRemovalActions.setIsPhotoTooBigPopupShow(true));
        continue;
      }
      if (newImages.length >= MaxPhotoUpload) break;
      const uri = await readURIFromFile(file);
      // 各画像ごとに新しいstubIdを生成してユニークさを保証
      const uniqueStubId = getRandId();
      newImages.push({
        stubId: uniqueStubId,
        file,
        uri,
        isProcessed: false,
        isProcessedFail: false,
        maxRegenCount: MaxRegenCount,
      });
    }
    thunkAPI.dispatch(itemRemovalActions.setItemImages(newImages));

    // 画像が追加された場合、最後に追加された画像を選択状態にする
    if (newImages.length > itemImages.length) {
      const lastAddedImage = newImages[newImages.length - 1];
      thunkAPI.dispatch(itemRemovalActions.setSelectedItemImage(lastAddedImage.stubId));
    }

    // stubIdをstateに保存して後でprocessItemRemovalで使用できるようにする
    if (stubId) {
      thunkAPI.dispatch(itemRemovalActions.setStubId(parseInt(stubId, 10) || getRandId()));
    }
  }
);

// 家具削除処理を実行する非同期アクション
const processItemRemoval = createAsyncThunk<void, NavigateFunction, { state: RootState }>(
  "processItemRemoval",
  async (navigate, thunkAPI) => {
    const { stubId, itemImages } = thunkAPI.getState().itemRemoval;
    // 処理前に未処理の画像があるか確認
    const unprocessedImages = itemImages.filter(img => !img.isProcessed);

    if (unprocessedImages.length === 0) {
      return;
    }

    thunkAPI.dispatch(itemRemovalActions.setIsLoading(true));

    // タイマー処理
    let runTime = 0;
    thunkAPI.dispatch(itemRemovalActions.setRuntime(runTime));
    const timer = setInterval(() => {
      thunkAPI.dispatch(itemRemovalActions.setRuntime(runTime += 1));
    }, 1000);

    try {
      // 画像処理実行
      const results = await itemImagesProcess(thunkAPI, itemImages, stubId.toString());

      // 成功した画像の数を確認
      const successCount = results.filter(r => r).length;
      if (successCount > 0) {
        // thunkAPI.dispatch(toastActions.toastSuccess(`${successCount}枚の画像処理が完了しました`));
      } else {
        // thunkAPI.dispatch(toastActions.toastError("画像処理に失敗しました"));
      }
    } catch (e) {
    } finally {
      // 処理終了時の後処理
      clearInterval(timer);
      thunkAPI.dispatch(itemRemovalActions.setIsLoading(false));
      thunkAPI.dispatch(currentUserActions.getCurrentUser(navigate));
    }
  }
);


// すべての画像を処理する関数
async function itemImagesProcess(
  thunkAPI: ThunkAPIType,
  itemImages: ItemImage[],
  stubId: string
): Promise<boolean[]> {
  // 並列処理に変更（すべての画像を同時に処理する）

  const unprocessedImages = itemImages.filter(img => !img.isProcessed);


  // 最初の画像を選択状態に設定（未選択の場合のみ）
  if (unprocessedImages.length > 0 && thunkAPI.getState().itemRemoval.selectedItemImage === undefined) {
    thunkAPI.dispatch(itemRemovalActions.setSelectedItemImage(unprocessedImages[0].stubId));
  }

  // 各画像の処理をPromiseの配列として準備
  const processingPromises = unprocessedImages.map(itemImage => {

    // 各画像を並列処理
    return itemImageProcess(itemImage, thunkAPI, stubId)
      .then(result => {
        return { stubId: itemImage.stubId, success: result };
      })
      .catch(error => {
        console.error(`itemImagesProcess - 画像処理エラー:`, {
          stubId: itemImage.stubId,
          error
        });
        return { stubId: itemImage.stubId, success: false };
      });
  });

  // すべての処理が完了するのを待つ（各処理は独立して実行される）
  const results = await Promise.all(processingPromises);

  // デバッグ：すべての処理完了後の最終状態
  const finalState = thunkAPI.getState().itemRemoval;

  // booleanの配列に変換して返す
  return results.map(r => r.success);
}

// 単一の家具画像を処理する関数
function itemImageProcess(
  itemImage: ItemImage,
  thunkAPI: ThunkAPIType,
  stubId: string
): Promise<boolean> {
  return new Promise((resolve) => {
    const { file } = itemImage;

    // パラメータの作成 - 直接FormDataを使用するよう変更
    const params = {
      formData: {
        stub_id: stubId,
        item_image: file
      }
    };

    // processItemRemovalメソッドを使用
    (async () => {
      try {

        // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
        let res;
        if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
          // モックレスポンスを使用
          await new Promise(resolve => setTimeout(resolve, 1500)); // API呼び出しをシミュレート
          const mockItemRemovalId = Math.floor(Math.random() * 10000) + 1;
          const mockBeforeImageId = itemImage.id || Math.floor(Math.random() * 10000) + 100;
          res = mockTestConfig.createMockServerResponse(mockItemRemovalId, mockBeforeImageId);
        } else {
          // 実際のAPIを使用
          res = await ItemRemovalService.processItemRemoval(params);
        }
        // TODO: テストが終わったら消す - サーバーレスポンスモックの終了


        const processResult = processServerResponse(
          itemImage.stubId,
          res,
          thunkAPI
        );

        if (!processResult) {
          resolve(false);
          return;
        }
        resolve(true);
      } catch (error) {
        const errorMessage = typeof error === 'object' && error !== null && 'message' in error
          ? error.message
          : '不明なエラーが発生しました';
        // thunkAPI.dispatch(toastActions.toastError(errorMessage));

        // エラー状態を設定
        const newItemImages = thunkAPI.getState().itemRemoval.itemImages.map((img: ItemImage) => {
          if (img.stubId !== itemImage.stubId) return img;
          return {
            ...img,
            isProcessedFail: true
          };
        });
        thunkAPI.dispatch(itemRemovalActions.setItemImages(newItemImages));
        resolve(false);
      }
    })();
  });
}

// サーバーからの応答を処理する関数
function processServerResponse(
  stubId: number,
  res: ItemRemovalServerResponse,
  thunkAPI: ThunkAPIType
): boolean {
  const { itemImages, afterImages, selectedItemImage } = thunkAPI.getState().itemRemoval;


  // ヘッダーからrender_idを取得
  const render_id = res.headers?.['x-render-id'] || '';

  // 新しいitemImages配列を作成
  const newItemImages = itemImages.map((itemImage: ItemImage) => {
    // この画像の処理でなければスキップ
    if (itemImage.stubId !== stubId) return itemImage;

    // モックモードの場合
    if (OpenAPI.USE_MOCK) {
      const mockId = res.id || 999;
      const randomImage = regeneratedImages[Math.floor(Math.random() * regeneratedImages.length)];

      return {
        ...itemImage,
        isProcessedFail: false,
        isProcessed: true,
        id: mockId,
        serverURI: randomImage,
        render_id
      };
    }

    // 実際のAPI処理
    if (!res.success) {
      const errorMessage = res.message || "画像の処理に失敗しました";
      thunkAPI.dispatch(toastActions.toastError(errorMessage));

      return {
        ...itemImage,
        isProcessedFail: true
      };
    }

    // 成功時 - この時点でstubIdと紐づける
    return {
      ...itemImage,
      isProcessedFail: false,
      isProcessed: true,
      id: res.item_removal_id,
      before_image_id: res.before_image_id,
      serverURI: res.after_image_url,
      render_id
    };
  });

  thunkAPI.dispatch(itemRemovalActions.setItemImages(newItemImages));

  // 処理に失敗した場合はここで終了
  if (!res.success || !res.item_removal_id) {
    return false;
  }

  // 既存画像を保持したまま、新しい画像を追加
  const newAfterImages = [...afterImages];

  // 実際のAPIレスポンスから処理済み画像を作成
  // APIレスポンスには単一の画像情報のみが含まれる
  if (res.after_image_id && res.after_image_url) {
    const newAfterImageId = res.after_image_id;
    const newAfterImage = {
      id: newAfterImageId,
      item_image_id: res.item_removal_id, // サーバーから返されるID
      before_image_id: res.before_image_id, // 元画像ID
      serverURI: res.after_image_url,
      fileName: res.after_image_file_name || res.file_name || `After Image`,
      stubId, // 元画像のstubIdを設定（重要：これが主な関連付け手段）
    };
    newAfterImages.push(newAfterImage);

  }

  // 処理済み画像を更新
  thunkAPI.dispatch(itemRemovalActions.setAfterImages(newAfterImages));

  // ウォーターマークが有効な場合、新しく追加された画像に自動的にウォーターマークを適用
  const { watermarkGlobalEnabled } = thunkAPI.getState().itemRemoval;
  if (watermarkGlobalEnabled && res.after_image_id && res.after_image_url) {
    const serviceSettings = selectServiceSettings(thunkAPI.getState());

    if (serviceSettings && isWatermarkEnabled(serviceSettings)) {
      // ウォーターマークを適用
      thunkAPI.dispatch(applyWatermarkToImage({
        imageId: res.after_image_id,
        imageUrl: res.after_image_url
      }));
    }
  }

  // 新しく処理した画像が現在選択中の画像と一致していれば、自動的に処理後画像を選択
  const currentSelectedItemImage = thunkAPI.getState().itemRemoval.selectedItemImage;
  if (currentSelectedItemImage === stubId && res.after_image_id) {
    thunkAPI.dispatch(itemRemovalActions.setSelectedAfterImage(res.after_image_id));
  }

  return true;
}

// selected項目の削除
const deleteSelectedItem = createAsyncThunk<void, void, { state: RootState }>(
  "deleteSelectedItem",
  async (_, thunkAPI) => {
    const { selectedItemImage, itemImages } = thunkAPI.getState().itemRemoval;
    if (selectedItemImage === undefined) return;

    // 選択された項目を除外した新しい配列を作成
    const newItemImages = itemImages.filter(
      (img: ItemImage) => img.stubId !== selectedItemImage
    );

    // 状態を更新
    thunkAPI.dispatch(itemRemovalActions.setItemImages(newItemImages));
    thunkAPI.dispatch(itemRemovalActions.setSelectedItemImage(undefined));
  }
);

// 全項目の削除
const deleteAllItems = createAsyncThunk<void, void, { state: RootState }>(
  "deleteAllItems",
  async (_, thunkAPI) => {
    thunkAPI.dispatch(itemRemovalActions.clearData());
  }
);

// 処理済み画像のクリア
const clearSelectedAfterImage = createAsyncThunk<void, void, { state: RootState }>(
  "clearSelectedAfterImage",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState().itemRemoval;
    const { selectedItemImage, itemImages } = state;
    if (selectedItemImage === undefined) return;

    const selectedImg = itemImages.find((img) => img.stubId === selectedItemImage);
    if (!selectedImg || !selectedImg.id) return;

    try {
      // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
      let clearRes;
      if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
        // モックレスポンスを使用
        await new Promise(resolve => setTimeout(resolve, 1000)); // API呼び出しをシミュレート
        clearRes = { success: true };
      } else {
        // 実際のAPIを使用
        clearRes = await ItemRemovalService.clearAfterImages(selectedImg.id);
      }
      // TODO: テストが終わったら消す - サーバーレスポンスモックの終了

      if (clearRes.success) {
        thunkAPI.dispatch(
          toastActions.toastSuccess("処理済み画像をクリアしました")
        );
      } else {
        thunkAPI.dispatch(
          toastActions.toastError(clearRes.message || "処理済み画像のクリアに失敗しました")
        );
      }
    } catch (error) {
      const errorMessage = typeof error === 'object' && error !== null && 'message' in error
        ? error.message
        : '処理済み画像のクリアに失敗しました';

      thunkAPI.dispatch(
        toastActions.toastError(errorMessage)
      );
    }
  }
);

// 処理済み画像の再生成
const regenSelectedAfterImage = createAsyncThunk<void, void, { state: RootState }>(
  "regenSelectedAfterImage",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState().itemRemoval;
    const { selectedItemImage, itemImages } = state;

    // selectedItemImageがない場合は、最初の画像を選択
    if (selectedItemImage === undefined) {
      if (itemImages.length > 0) {
        const firstImg = itemImages[0];
        thunkAPI.dispatch(itemRemovalActions.setSelectedItemImage(firstImg.stubId));
        return thunkAPI.dispatch(itemRemovalActions.regenSelectedAfterImage());
      }
      return;
    }

    const selectedImg = itemImages.find((img) => img.stubId === selectedItemImage);

    if (!selectedImg.id) {
      thunkAPI.dispatch(toastActions.toastError("処理済みでない画像は再生成できません"));
      return;
    }

    thunkAPI.dispatch(itemRemovalActions.setSelectedRegenImage(selectedImg.stubId));
    thunkAPI.dispatch(itemRemovalActions.setIsLoading(true));

    // runTimeを0から始める - タイマー間隔を500ミリ秒に変更
    let runTime = 0;
    thunkAPI.dispatch(itemRemovalActions.setRuntime(runTime));
    const timer = setInterval(() => {
      thunkAPI.dispatch(itemRemovalActions.setRuntime(runTime += 0.5)); // 0.5秒ずつ増加
    }, 500); // 500ミリ秒ごとに更新

    try {
      // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
      let regenRes;
      if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
        // モックレスポンスを使用
        await new Promise(resolve => setTimeout(resolve, 1500)); // API呼び出しをシミュレート
        const mockUrl = mockTestConfig.MOCK_IMAGE_URLS[Math.floor(Math.random() * mockTestConfig.MOCK_IMAGE_URLS.length)];
        regenRes = {
          success: true,
          after_image_id: Math.floor(Math.random() * 100000) + 5000,
          after_image_url: mockUrl,
          after_image_file_name: `mock_image_${Date.now()}.jpg`,
          file_name: `item_removed_regen_${Date.now()}.jpg`
        };
      } else {
        // 実際のAPIを使用
        regenRes = await ItemRemovalService.regenAfterImages(selectedImg.before_image_id);
      }
      // TODO: テストが終わったら消す - サーバーレスポンスモックの終了

      if (regenRes.success && regenRes.after_image_id && regenRes.after_image_url) {
        // API成功かつ画像データが正常に返された場合のみカウントを減らす
        thunkAPI.dispatch(itemRemovalActions.subtractMaxRegen(selectedImg.stubId));
        thunkAPI.dispatch(
          toastActions.toastSuccess("処理済み画像を再生成しました")
        );

        const { afterImages } = thunkAPI.getState().itemRemoval;
        const newAfterImageId = regenRes.after_image_id;

        // 既存画像を保持したまま、新しい画像を追加
        const newAfterImages = [...afterImages];

        // 新しい処理済み画像を追加
        const newAfterImage = {
            id: newAfterImageId,
            item_image_id: selectedImg.id,
            before_image_id: selectedImg.before_image_id,
            serverURI: regenRes.after_image_url,
            fileName: regenRes.after_image_file_name || regenRes.file_name || `Regenerated Image`,
            stubId: selectedImg.stubId,
        };
        newAfterImages.push(newAfterImage);

        // 状態を更新
        thunkAPI.dispatch(itemRemovalActions.setAfterImages(newAfterImages));

        // ウォーターマークが有効な場合、新しく生成された画像に自動的にウォーターマークを適用
        const { watermarkGlobalEnabled } = thunkAPI.getState().itemRemoval;
        if (watermarkGlobalEnabled && regenRes.after_image_id && regenRes.after_image_url) {
            const serviceSettings = selectServiceSettings(thunkAPI.getState());
            const hasWatermarkSettings = serviceSettings &&
              serviceSettings.watermark_type !== WATERMARK_TYPE.NONE && (
                (serviceSettings.watermark_type === WATERMARK_TYPE.INDIVIDUAL_INPUT &&
                 serviceSettings.individual_input_text &&
                 serviceSettings.individual_input_is_visible) ||
                (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO &&
                 serviceSettings.corporate_logo_file_data &&
                 serviceSettings.corporate_logo_is_visible !== false)
              );

          if (hasWatermarkSettings) {
            // ウォーターマークを適用
            thunkAPI.dispatch(applyWatermarkToImage({
              imageId: regenRes.after_image_id,
              imageUrl: regenRes.after_image_url
            }));
          }
        }

        // 新しく生成した画像を選択状態にする
        thunkAPI.dispatch(itemRemovalActions.setSelectedAfterImage(newAfterImageId));
      } else {
        thunkAPI.dispatch(
          toastActions.toastError(regenRes.message || "再生成に失敗しました")
        );
      }
    } catch (error) {
      const errorMessage = typeof error === 'object' && error !== null && 'message' in error
        ? error.message
        : '処理済み画像の再生成に失敗しました';

      thunkAPI.dispatch(
        toastActions.toastError(errorMessage)
      );
    } finally {
      thunkAPI.dispatch(itemRemovalActions.setSelectedRegenImage(undefined));
      thunkAPI.dispatch(itemRemovalActions.setIsLoading(false));
      clearInterval(timer);  // タイマーをクリア
    }
  }
);

const initialState: ItemRemovalState = {
  isLoading: false,
  stubId: getRandId(),
  selectedItemImage: undefined,
  selectedAfterImage: undefined,
  itemImages: [],
  afterImages: [],
  isConfirmPopupShow: false,
  isPhotoTooBigPopupShow: false,
  isRegenPopupShow: false,
  isClearAllPopupShow: false,
  maxExecTimePerImage: 40,
  runTime: 0,
  selectedRegenImage: undefined,
  afterImageRef: null,
  beforeImageRef: null,
  // ウォーターマーク関連
  watermarkGlobalEnabled: false,
  processingWatermarks: [],
};


// ウォーターマーク適用の非同期処理
const applyWatermarkToImage = createAsyncThunk<
  { imageId: number; watermarkedURI: string },
  { imageId: number; imageUrl: string },
  { state: RootState }
>(
  'itemRemoval/applyWatermarkToImage',
  async ({ imageId, imageUrl }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch
    const state = thunkAPI.getState()

    dispatch(itemRemovalActions.setImageWatermarkProcessing({ imageId, isProcessing: true }))

    try {
      // サービス設定からウォーターマーク設定を取得
      const watermarkSettings = selectServiceSettings(state)

      // ウォーターマークが有効かチェック
      if (!isWatermarkEnabled(watermarkSettings)) {
        throw new Error('Watermark is not enabled in service settings')
      }

      // 画像をBlobに変換
      const blob = await fetchImageAsBlob(imageUrl)

      // ウォーターマーク適用（現在の実装に合わせた設定）
      const watermarkOptions = convertServiceSettingsToWatermarkOptions(watermarkSettings)

      const result = await applyWatermark(
        blob,
        watermarkOptions,
        blob.size / (1024 * 1024)
      )

      // Blob URLではなくData URLを作成（CSP対策）
      const watermarkedURI = await blobToDataURL(result.blob)

      dispatch(itemRemovalActions.setImageWatermarkStatus({
        imageId,
        enabled: true,
        watermarkedURI
      }))

      return { imageId, watermarkedURI }
    } catch (error) {
      console.error('Failed to apply watermark:', error)
      dispatch(toastActions.toastError('ウォーターマークの適用に失敗しました'))
      throw error
    } finally {
      dispatch(itemRemovalActions.setImageWatermarkProcessing({ imageId, isProcessing: false }))
    }
  }
)

// 全画像へのウォーターマーク一括適用/解除
const toggleAllWatermarks = createAsyncThunk<
  void,
  boolean,
  { state: RootState }
>(
  'itemRemoval/toggleAllWatermarks',
  async (enabled, thunkAPI) => {
    const state = thunkAPI.getState()
    const dispatch = thunkAPI.dispatch
    const { afterImages } = state.itemRemoval

    dispatch(itemRemovalActions.setWatermarkGlobalEnabled(enabled))

    if (enabled) {
      // 全画像にウォーターマーク適用
      const promises = afterImages.map((image: AfterImage) =>
        dispatch(applyWatermarkToImage({
          imageId: image.id,
          imageUrl: image.serverURI
        }))
      )

      await Promise.all(promises)
    } else {
      // 全画像のウォーターマークを除去
      // Data URLの場合はrevokeObjectURLは不要
      dispatch(itemRemovalActions.clearAllWatermarks())
    }
  }
)

// スライスの作成
const itemRemovalSlice = createSlice({
  name: "itemRemoval",
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStubId: (state, action: PayloadAction<number>) => {
      state.stubId = action.payload;
    },
    setSelectedItemImage: (state, action: PayloadAction<number | undefined>) => {

      state.selectedItemImage = action.payload;

      // 選択された元画像を見つける
      if (action.payload !== undefined) {
        const selectedImage = state.itemImages.find(img => img.stubId === action.payload);


        // 処理済みの場合、対応する処理結果画像を自動選択
        if (selectedImage && selectedImage.isProcessed) {
          // マッチング条件を定義
          const matchingAfterImages = state.afterImages.filter(img => {
            // before_image_idによる紐付け - 同じ元画像から生成された画像
            return img.before_image_id === selectedImage.before_image_id && selectedImage.before_image_id !== undefined;
          });


          if (matchingAfterImages.length > 0) {
            // 最新の処理後画像を選択（IDが大きい方が新しい）
            const latestImage = matchingAfterImages.reduce((latest, current) => {
              return current.id > latest.id ? current : latest;
            }, matchingAfterImages[0]);

            state.selectedAfterImage = latestImage.id;
          } else {
            // マッチする画像がない場合は選択をクリア
            state.selectedAfterImage = undefined;
          }
        } else {
          // 処理済みでない場合は選択をクリア
          state.selectedAfterImage = undefined;
        }
      } else {
        // 選択がクリアされた場合
        state.selectedAfterImage = undefined;
      }
    },
    setAfterImages: (state, action: PayloadAction<AfterImage[]>) => {
      state.afterImages = action.payload;
    },
    setIsPhotoTooBigPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isPhotoTooBigPopupShow = action.payload;
    },
    setIsRegenPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isRegenPopupShow = action.payload;
    },
    setIsClearAllPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isClearAllPopupShow = action.payload;
    },
    setRuntime: (state, action: PayloadAction<number>) => {
      state.runTime = action.payload;
    },
    setSelectedRegenImage: (state, action: PayloadAction<number | undefined>) => {
      state.selectedRegenImage = action.payload;
    },
    regenSelectedAfterImage: (state) => {
      // 空の実装 - 実際の実装は非同期アクションクリエーターでされています
    },
    subtractMaxRegen: (state, action: PayloadAction<number>) => {
      const stubId = action.payload;
      state.itemImages.forEach(image => {
        if (image.stubId !== stubId) return;
        if (image.maxRegenCount !== undefined) {
          image.maxRegenCount -= 1;
        }
      });
    },
    clearData: (state) => {
      state.stubId = getRandId();
      state.selectedItemImage = undefined;
      state.selectedAfterImage = undefined;
      state.itemImages = [];
      state.afterImages = [];
      state.isLoading = false;
      state.isConfirmPopupShow = false;
      state.isPhotoTooBigPopupShow = false;
      state.isRegenPopupShow = false;
      state.isClearAllPopupShow = false;
      state.maxExecTimePerImage = 20; // 20秒に設定
      state.runTime = 0;
      state.selectedRegenImage = undefined;
      state.afterImageRef = null;
      state.beforeImageRef = null;
      // ウォーターマーク関連
      state.watermarkGlobalEnabled = false;
      state.processingWatermarks = [];
    },
    setItemImages: (state, action: PayloadAction<ItemImage[]>) => {
      state.itemImages = action.payload;
    },
    setSelectedAfterImage: (state, action: PayloadAction<number | undefined>) => {
      state.selectedAfterImage = action.payload;
    },
    setIsConfirmPopupShow: (state, action: PayloadAction<boolean>) => {
      state.isConfirmPopupShow = action.payload;
    },
    setAfterImageRef: (state, action: PayloadAction<HTMLImageElement | null>) => {
      state.afterImageRef = action.payload;
    },
    setBeforeImageRef: (state, action: PayloadAction<HTMLImageElement | null>) => {
      state.beforeImageRef = action.payload;
    },
    // ウォーターマーク関連
    setWatermarkGlobalEnabled: (state, action: PayloadAction<boolean>) => {
      state.watermarkGlobalEnabled = action.payload;
    },
    setImageWatermarkStatus: (state, action: PayloadAction<{
      imageId: number,
      enabled: boolean,
      watermarkedURI?: string,
    }>) => {
      const image = state.afterImages.find(img => img.id === action.payload.imageId)
      if (image) {
        image.watermarkEnabled = action.payload.enabled
        if (action.payload.watermarkedURI !== undefined) {
          image.watermarkedURI = action.payload.watermarkedURI
        }
      }
    },
    setImageWatermarkProcessing: (state, action: PayloadAction<{
      imageId: number,
      isProcessing: boolean,
    }>) => {
      const image = state.afterImages.find(img => img.id === action.payload.imageId)
      if (image) {
        image.isProcessingWatermark = action.payload.isProcessing
      }
      // 処理中リストも更新
      if (action.payload.isProcessing) {
        if (!state.processingWatermarks.includes(String(action.payload.imageId))) {
          state.processingWatermarks.push(String(action.payload.imageId))
        }
      } else {
        state.processingWatermarks = state.processingWatermarks.filter(id => id !== String(action.payload.imageId))
      }
    },
    clearAllWatermarks: (state) => {
      state.afterImages.forEach(image => {
        if (image.watermarkedURI) {
          // Data URLを使用しているため、特別なクリーンアップは不要
          image.watermarkedURI = undefined
        }
        image.watermarkEnabled = false
        image.isProcessingWatermark = false
      })
      state.processingWatermarks = []
    },
  },
});

// アクションとリデューサーをエクスポート
export const itemRemovalActions = {
  ...itemRemovalSlice.actions,
  addItemImages,
  processItemRemoval,
  deleteSelectedItem,
  deleteAllItems,
  clearSelectedAfterImage,
  regenSelectedAfterImage,
  applyWatermarkToImage,
  toggleAllWatermarks,
};

export default itemRemovalSlice.reducer;